import { Box, Flex, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { AccessBadge } from '#features/media/AccessBadge';
import { useMicDenialHint } from '#features/media/useMicDenialHint';
import { envConfig } from '#constants/envConfig';
import { openAppSettings } from '#features/notifications/utils/deviceSettings';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { IS_IOS_AUDIO_ROUTED } from '#features/media-window/audioRouting';
import {
  CORNER_PROPS,
  initialWindowMode,
  MOBILE_ANCHOR_PROPS,
  MOBILE_BREAKPOINT_PX,
} from '#features/media-window/model';
import { useCornerDrag } from '#features/media-window/useCornerDrag';
import { Button, MicOffIcon, MicOnIcon } from '#features/ui';
import { Avatar, AvatarGroup } from '#features/ui/components/Avatar';
import { useVoiceActions } from '#features/voice/hooks/useVoiceActions';
import { useVoiceRoom } from '#features/voice/hooks/useVoiceRoom';
import { useVoiceStore, type VoiceMember } from '#features/voice/model/voiceStore';
import { useBottomSpaceVar } from '#hooks/useBottomSpaceVar';
import { usePushToTalk } from './usePushToTalk';
import { useVoiceDuration } from './useVoiceDuration';
import { VoiceDevicePicker } from './VoiceDevicePicker';
import { VoiceParticipantRow } from './VoiceParticipantRow';
import { VoiceQualityIcon } from './VoiceQualityIcon';

/**
 * Плавающее окно разговора: живёт поверх страниц, пока человек в голосе.
 *
 * Показывается всегда, в том числе на странице той самой сессии. Прежде оно там пряталось в пользу
 * блока в карточке — и это оказалось неверно: человек нажимает «войти», а на экране не появляется
 * ничего нового, потому что единственный видимый отклик прячется в той же карточке, куда он уже
 * смотрит. Окно и карточка не спорят: карточка отвечает на «что с комнатой», окно — на «что с моим
 * микрофоном». Ровно так же ведёт себя звонок 1:1: оверлей висит поверх и на странице того чата,
 * из которого звонили.
 *
 * Скрыть окно отдельной кнопкой нельзя — закрытие означает выход из голоса. Иначе разговор
 * продолжается, а следов на экране не остаётся, и человек говорит, не понимая, что его слышат.
 */
export const VoiceWidget = () => {
  const { t } = useLingui();
  const micDenialHint = useMicDenialHint();
  /**
   * Список устройств раскрывается сам сразу после выданного разрешения: человек только что
   * разрешил микрофон и следующим делом выбирает, каким именно говорить, — гарнитурой, а не
   * микрофоном монитора.
   */
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const phase = useVoiceStore((s) => s.phase);
  const room = useVoiceStore((s) => s.room);
  const members = useVoiceStore((s) => s.members);
  const micEnabled = useVoiceStore((s) => s.micEnabled);
  const windowMode = useVoiceStore((s) => s.windowMode);
  const corner = useVoiceStore((s) => s.corner);
  const mobileAnchor = useVoiceStore((s) => s.mobileAnchor);
  const setWindowMode = useVoiceStore((s) => s.setWindowMode);
  const setCorner = useVoiceStore((s) => s.setCorner);
  const setMobileAnchor = useVoiceStore((s) => s.setMobileAnchor);
  const setMicEnabled = useVoiceStore((s) => s.setMicEnabled);
  const pushToTalk = useVoiceStore((s) => s.pushToTalk);
  const setPushToTalk = useVoiceStore((s) => s.setPushToTalk);

  const { profile } = useProfile();
  const selfId = profile?.id ?? null;
  const { kick, mute } = useVoiceActions();

  const {
    audioContainerRef,
    micDenied,
    requestMicAccess,
    audioBlocked,
    unblockAudio,
    leave,
    setParticipantVolume,
    setParticipantMuted,
    listDevices,
    switchDevice,
  } = useVoiceRoom();
  const duration = useVoiceDuration(phase === 'connected' || phase === 'reconnecting');
  usePushToTalk({ enabled: phase === 'connected', pushToTalk, micEnabled, setMicEnabled });

  const windowRef = useRef<HTMLDivElement | null>(null);
  // Узел нужен и как ref (перетаскивание по углам), и как состояние (высота полосы уезжает в
  // CSS-переменную, а эффект обязан пересчитаться в момент появления и исчезновения окна).
  const [windowNode, setWindowNode] = useState<HTMLDivElement | null>(null);
  const attachWindow = useCallback((node: HTMLDivElement | null) => {
    windowRef.current = node;
    setWindowNode(node);
  }, []);
  const onGripPointerDown = useCornerDrag({
    ref: windowRef,
    canDrag: windowMode === 'window',
    anchor: mobileAnchor,
    onCollapse: () => setWindowMode('mini'),
    onCorner: setCorner,
    onAnchor: setMobileAnchor,
  });

  const isCollapsed = windowMode === 'mini';
  const isFull = windowMode === 'full';
  // Угол — десктопная история: на телефоне окно всегда внизу, над навигацией, и перетаскивать его
  // некуда. Проверка ширины безопасна на сервере: пока человек не в голосе, окна нет вовсе.
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= MOBILE_BREAKPOINT_PX;

  /**
   * Свёрнутое окно на телефоне — не плавающая карточка, а полоса разговора: страница оставляет под
   * неё место, и перекрыть кнопку она физически не может. Раскрытое окно место не резервирует —
   * это временный слой, который человек сам открыл и сам закроет.
   */
  const isBar = !isDesktop && isCollapsed;
  // Место оставляем только под полосу, реально стоящую внизу: поднятая наверх ничего не закрывает,
  // а резерв под неё оторвал бы страницу от нижнего края на пустом месте.
  useBottomSpaceVar('--voice-bar-space', isBar && mobileAnchor === 'bottom' ? windowNode : null);

  // Аудио должно жить независимо от того, свёрнуто окно или раскрыто: элементы <audio> вынесены
  // наружу и не размонтируются вместе с панелью.
  const audioSink = <Box ref={audioContainerRef} display="none" aria-hidden="true" />;

  useEffect(() => {
    if (phase === 'idle') {
      setWindowMode(initialWindowMode());
    }
  }, [phase, setWindowMode]);

  if (phase === 'idle' || !room) {
    return null;
  }

  const connected = members.filter((member) => member.connected);
  const speaking = connected.find((member) => member.speaking);
  const isHost = Boolean(selfId && room.hostId === selfId);

  const placement = isFull
    ? { inset: 0 }
    : isDesktop
      ? CORNER_PROPS[corner]
      : {
          left: 3,
          right: 3,
          // Внизу окно стоит НАД нижней навигацией, а не поверх неё: перекрывать единственный
          // способ уйти со страницы нельзя. Отступы общие со звонком 1:1 — иначе два окна с одной
          // ролью стоят на разной высоте. Верхний край — то, куда окно можно увести жестом, когда
          // внизу оно мешает писать.
          ...MOBILE_ANCHOR_PROPS[mobileAnchor],
        };

  return (
    <>
      {audioSink}
      <Box
        ref={attachWindow}
        data-testid="voice-widget"
        data-window-mode={windowMode}
        position="fixed"
        // Слой окна — `modal`, тот же, что у оверлея звонка 1:1: два окна одной природы обязаны
        // жить на одной высоте. На `popover` (1500) окно оказывалось выше любой модалки (её слой —
        // `modal`, 1400), и на телефоне это было видно буквально: подтверждение «выгнать из
        // голоса» приезжает шторкой снизу, окно накрывало её заголовок и текст, и от вопроса
        // «выгнать такого-то?» оставались одни кнопки. Теперь при равном слое побеждает порядок в
        // DOM — портал модалки приезжает позже, — и шторка ложится поверх окна.
        zIndex="modal"
        {...placement}
        w={isFull ? 'auto' : { base: 'auto', md: isCollapsed ? '344px' : '340px' }}
        maxH={isFull ? 'none' : { base: '70vh', md: '75vh' }}
        display="flex"
        flexDirection="column"
        bg="bg.surface"
        borderRadius={isFull ? 'none' : isCollapsed ? { base: '2xl', md: 'full' } : 'xl'}
        boxShadow="xl"
        overflow="hidden"
        animation="fadeIn 200ms cubic-bezier(.32,.72,0,1)"
        css={{
          overscrollBehavior: 'none',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {isCollapsed ? (
          <Flex align="center" gap={2.5} p="6px" pr={2}>
            {/* Левая половина полосы — она же ручка: за неё полосу уводят от нижнего края наверх,
                когда она встала поверх поля ввода. Жест не навешен на всю полосу, потому что
                `preventDefault` на pointerdown отнимает нажатия у кнопок микрофона и раскрытия. */}
            <Flex
              data-testid="voice-window-grip"
              align="center"
              gap={2.5}
              minW={0}
              flex="1"
              userSelect="none"
              cursor={{ base: 'default', md: 'grab' }}
              onPointerDown={onGripPointerDown}
              css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
              {/* Аватары и в свёрнутом виде: они отвечают на «кто там», не занимая ни строки текста. */}
              <VoiceAvatars members={connected} max={2} />
              <Box flex="none" minW={0}>
                <Text
                  textStyle="label"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {/* Имя говорящего — только когда оно известно: подставлять сюда UUID вместо ника
                      хуже, чем не называть человека вовсе. */}
                  {speaking?.displayName
                    ? `${t`post:voice.room.speaking`} ${speaking.displayName}`
                    : `${connected.length} ${t`post:voice.inVoice`}`}
                </Text>
                <Text textStyle="xs" color="fg.muted" fontVariantNumeric="tabular-nums">
                  {duration}
                </Text>
              </Box>
            </Flex>

            <Flex ml="auto" align="center" gap={1} flex="none">
              <MicButton micEnabled={micEnabled} onClick={() => setMicEnabled(!micEnabled)} />
              <Button
                variant="ghost"
                size="sm"
                minW="44px"
                px={0}
                aria-label={t`post:voice.widget.expand`}
                onClick={() => setWindowMode('window')}
              >
                <ChevronUp />
              </Button>
            </Flex>
          </Flex>
        ) : (
          <>
            {/* Полоска-ручка: за неё окно таскают по углам на десктопе, ею же смахивают вниз на
                телефоне. Отдельным элементом, а не заголовком целиком, — иначе перетаскивание
                отнимает нажатия у кнопок, которые в заголовке стоят. */}
            <Box
              data-testid="voice-window-grip"
              h={{ base: '18px', md: '14px' }}
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor={{ base: 'default', md: windowMode === 'window' ? 'grab' : 'default' }}
              userSelect="none"
              onPointerDown={onGripPointerDown}
              css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
              <Box w="32px" h="3px" borderRadius="full" bg="whiteAlpha.300" />
            </Box>

            <Flex align="flex-start" gap={2.5} px={3.5} pb={2.5}>
              <Box flex={1} minW={0}>
                <Flex align="center" gap={1.5}>
                  <Text textStyle="label" color="status.live.fg">
                    {t`post:voice.widget.title`}
                  </Text>
                  <Box w="5px" h="5px" borderRadius="full" bg="status.live.solid" />
                  <Text textStyle="micro" color="fg.muted" fontVariantNumeric="tabular-nums">
                    {duration}
                  </Text>
                </Flex>
                {room.gameName ? (
                  <Text
                    textStyle="label"
                    mt={1}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {room.gameName}
                  </Text>
                ) : null}
                <Text textStyle="xs" color="fg.muted" mt={0.5} whiteSpace="nowrap">
                  {room.hostName ? `${room.hostName} · ` : ''}
                  <Box as="span" fontVariantNumeric="tabular-nums">
                    {connected.length} {t`post:voice.of`} {room.maxParticipants ?? connected.length}
                  </Box>
                </Text>
              </Box>

              <Flex gap={0.5} flex="none">
                {/* Хорошая связь молчит: постоянный зелёный значок — это шум, а не информация. */}
                <VoiceQualityIcon />
                <Button
                  variant="ghost"
                  size="xs"
                  minW="32px"
                  px={0}
                  aria-label={
                    isFull ? t`post:voice.widget.exitFullscreen` : t`post:voice.widget.fullscreen`
                  }
                  onClick={() => setWindowMode(isFull ? 'window' : 'full')}
                >
                  {isFull ? <MinimizeIcon /> : <MaximizeIcon />}
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  minW="32px"
                  px={0}
                  aria-label={t`post:voice.widget.collapse`}
                  onClick={() => setWindowMode('mini')}
                >
                  <ChevronDown />
                </Button>
              </Flex>
            </Flex>

            {/* Сжимается и прокручивается именно список — это единственный кусок окна, который
                растёт с составом. Пока он стоял на `flex: none`, полная комната выдавливала
                «Выйти» за нижнюю кромку окна, и выйти из голоса становилось нечем. */}
            <Box px={1.5} pb={1.5} flex="1 1 auto" minH={0} overflowY="auto">
              {/* Говорящие сверху, я — всегда последним: свой аватар искать не нужно, а вот кто
                  сейчас говорит, читается с одного взгляда. */}
              {connected
                .toSorted((a, b) => Number(b.speaking) - Number(a.speaking))
                .map((member) => (
                  <VoiceParticipantRow
                    key={member.userId}
                    member={member}
                    isSelf={member.userId === selfId}
                    onMuteForMe={setParticipantMuted}
                    onVolumeChange={setParticipantVolume}
                    // Модерация — только автору сессии и только на чужие строки.
                    onMuteForAll={
                      isHost ? (userId) => void mute(room.sessionId, userId) : undefined
                    }
                    onRemove={isHost ? (userId) => void kick(room.sessionId, userId) : undefined}
                  />
                ))}
            </Box>

            {/* Предупреждение занимает свою строку: на телефоне ряд «микрофон + текст + две
                кнопки» сдавливал подсказку до двух слов в строке — проверено на устройстве
                27.08, читать было невозможно. На широком экране всё остаётся одной строкой. */}
            <Flex
              align="center"
              flexWrap="wrap"
              gap={2}
              px={2.5}
              py={2.5}
              flexShrink={0}
              borderTop="1px solid"
              borderColor="border.subtle"
            >
              <Flex
                align="center"
                flex="none"
                borderRadius="lg"
                bg={micEnabled ? 'status.live.bg' : 'bg.emphasized'}
              >
                <MicButton
                  micEnabled={micEnabled}
                  needsAccess={micDenied}
                  onClick={() => {
                    // С меткой «доступа нет» включать нечего: без разрешения микрофон молча не
                    // включится, а кнопка загорится зелёным и снова соврёт. Сначала спрашиваем.
                    if (micDenied) {
                      // Доступ дали — сразу показываем список устройств: следующий вопрос
                      // человека всегда «а тем ли микрофоном я говорю», и полоска уровня
                      // отвечает на него здесь же.
                      void (async () => {
                        if (await requestMicAccess()) {
                          setDevicePickerOpen(true);
                        }
                      })();
                      return;
                    }
                    setMicEnabled(!micEnabled);
                  }}
                  bare
                />
                {/* Выбор устройства прячем только там, где он ничего не решает: на iOS маршрут
                    звука назначает система. На Android микрофон переключать можно и нужно —
                    гарнитура и микрофон телефона там живут одновременно. */}
                {IS_IOS_AUDIO_ROUTED ? null : (
                  <VoiceDevicePicker
                    active={micEnabled}
                    listDevices={listDevices}
                    onSelect={switchDevice}
                    open={devicePickerOpen}
                    onOpenChange={setDevicePickerOpen}
                    pushToTalk={pushToTalk}
                    onPushToTalkChange={setPushToTalk}
                  />
                )}
              </Flex>
              {/* Отказ микрофона важнее подсказки и виден на любой ширине: подсказка про пробел —
                  удобство, а «тебя не слышат» — единственное, что человеку нужно знать прямо
                  сейчас. Молчал этот случай до сих пор целиком: флаг считался и никуда не шёл. */}
              <Text
                textStyle="xs"
                color={audioBlocked || micDenied ? 'fg.error' : 'fg.muted'}
                lineHeight={1.35}
                // На телефоне подсказка забирает строку целиком, а микрофон и кнопки уезжают на
                // следующую. Пока они делили одну строку вчетвером, текст сдавливало до двух слов
                // в строке — проверено на устройстве 27.08. На широком экране всё как было.
                flex={{ base: '1 0 100%', md: 1 }}
                order={{ base: -1, md: 0 }}
                minW={0}
                display={audioBlocked || micDenied ? 'block' : { base: 'none', md: 'block' }}
              >
                {/* Порядок важен: сначала то, чего человек не слышит, потом то, чего не слышат
                    его, и только потом удобства. Две беды разом бывают, но строка одна — и
                    занимать её должна та, которую человек может починить прямо сейчас. */}
                {audioBlocked
                  ? t`Sound is blocked by the browser`
                  : micDenied
                    ? micDenialHint
                    : pushToTalk
                      ? t`post:voice.widget.pushToTalkHint`
                      : null}
              </Text>
              {/* Единственная кнопка здесь, которой человек чинит свою тишину сам: Safari и
                  WKWebView разрешают воспроизведение только из жеста, поэтому ждать нечего —
                  надо нажать. Стоит перед «Выйти», потому что это и есть альтернатива уходу:
                  до сих пор в войсе выбора не было вовсе, человек просто не слышал никого. */}
              {audioBlocked && (
                <Button
                  size="sm"
                  variant="solid"
                  colorPalette="orange"
                  onClick={() => void unblockAudio()}
                  flex="none"
                >
                  {t`Turn on sound`}
                </Button>
              )}
              {/* Отказ в микрофоне человек видит именно здесь — уже внутри комнаты: до входа
                  системная проверка разрешений на Android ≤12 отвечает «выдано» даже на
                  отобранное (проверено на устройстве 27.08). Значит и кнопка нужна здесь, иначе
                  единственным выходом остаётся путь из четырёх шагов, набранный текстом. В вебе
                  такого экрана нет — там подсказка и остаётся подсказкой. */}
              {micDenied && envConfig.IS_NATIVE && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void openAppSettings()}
                  flex="none"
                >
                  {t`Open settings`}
                </Button>
              )}
              <Button size="sm" variant="outline" colorPalette="red" onClick={leave} flex="none">
                {t`post:voice.widget.leave`}
              </Button>
            </Flex>
          </>
        )}
      </Box>
    </>
  );
};

const MicButton = ({
  micEnabled,
  needsAccess,
  onClick,
  bare,
}: {
  micEnabled: boolean;
  /** Доступа к микрофону нет — на углу кнопки метка, а нажатие просит разрешение. */
  needsAccess?: boolean;
  onClick: () => void;
  /** Кнопка стоит внутри группы с выбором устройства — фон рисует группа, не она сама. */
  bare?: boolean;
}) => {
  const { t } = useLingui();
  return (
    <Button
      variant="ghost"
      size="sm"
      minW="44px"
      h="44px"
      px={0}
      borderRadius={bare ? 'lg' : 'full'}
      bg={bare ? 'transparent' : micEnabled ? 'status.live.bg' : 'bg.emphasized'}
      color={micEnabled ? 'status.live.fg' : 'fg.secondary'}
      // Метка садится на угол кнопки, поэтому кнопка обязана быть точкой отсчёта.
      position="relative"
      overflow="visible"
      aria-label={
        needsAccess
          ? t`media:access.mic.missing`
          : micEnabled
            ? t`post:voice.room.muteMic`
            : t`post:voice.room.unmuteMic`
      }
      onClick={onClick}
    >
      {micEnabled && !needsAccess ? <MicOnIcon fontSize="18px" /> : <MicOffIcon fontSize="18px" />}
      {needsAccess ? <AccessBadge /> : null}
    </Button>
  );
};

/** Стек аватаров участников — «+N» появляется сам, когда людей больше, чем помещается. */
const VoiceAvatars = ({ members, max }: { members: VoiceMember[]; max: number }) => {
  const shown = members.slice(0, max);
  const rest = members.length - shown.length;

  return (
    <AvatarGroup gap="0" spaceX="-2" size="xs" flex="none">
      {shown.map((member) => (
        <Avatar
          key={member.userId}
          size="xs"
          name={member.displayName ?? undefined}
          src={member.avatar ?? undefined}
          boxShadow={
            member.speaking
              ? '0 0 0 2px var(--chakra-colors-status-live-solid)'
              : '0 0 0 2px var(--chakra-colors-bg-surface)'
          }
        />
      ))}
      {rest > 0 ? (
        <Avatar
          size="xs"
          bg="bg.emphasized"
          color="fg.emphasized"
          boxShadow="0 0 0 2px var(--chakra-colors-bg-surface)"
          fallback={
            <Text textStyle="micro" fontVariantNumeric="tabular-nums">
              +{rest}
            </Text>
          }
        />
      ) : null}
    </AvatarGroup>
  );
};

const ChevronUp = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 15l-6-6-6 6" />
  </svg>
);

const ChevronDown = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const MaximizeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);

const MinimizeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
  </svg>
);
